/**
 * Бейджи у пунктов меню «Фитнес-центр» и «Прачечная».
 *
 * Зачем: ресепшен не должен заходить в каждый раздел, чтобы проверить
 * новые заказы. Бейдж сразу подсвечивает количество необработанных
 * (LaundryOrder.CREATED, FitnessBooking BOOKED + NEEDS_APPROVAL).
 *
 * Поллинг каждые 30 секунд — этого достаточно для оперативной работы
 * ресепшена, без агрессивной нагрузки на API.
 *
 * Backend endpoint: GET /api/v1/admin/sidebar/counters?platform_id=N
 */
import { makeAutoObservable } from 'mobx';
import { api } from '@/shared/api/api';
import { fetchInterDeptInboxCount } from '@/entities/concierge/interdept-requests-store';

interface Counters {
  /** Заказы консьержа, которые никто не взял в работу. */
  orders_new?: number;
  laundry_pending: number;
  fitness_pending: number;
  /** Локализованное название wellness-модуля у отеля.
   *  Каждый отель называет свой фитнес/спа/аквапарк по-своему — берём
   *  из FitnessConfig.display_name. Если пусто → fallback на «Wellness». */
  fitness_display_name?: Record<string, string> | null;
  /** Новые онлайн-заявки на конференц-залы (status=new). */
  conference_inquiries_new?: number;
}

class SidebarCountersStore {
  /** Новые заказы консьержа. Уведомление можно пропустить — баннер
   *  живёт секунды; бейдж висит, пока заказ не возьмут в работу. */
  ordersNew = 0;
  laundryPending = 0;
  fitnessPending = 0;
  /** Открытые внутренние заявки на отдел пользователя (inter-dept). */
  interdeptInbox = 0;
  /** Новые заявки гостей на конференц-залы. */
  conferenceInquiriesNew = 0;
  /** Имя пункта сайдбара «Фитнес-центр». Если в FitnessConfig
   *  display_name пусто — «Wellness». */
  fitnessLabel = 'Wellness';
  private platformId: number | null = null;
  private pollTimer: ReturnType<typeof setInterval> | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  /** Запустить поллинг для платформы. Если активная платформа сменилась —
   *  заново перезапустится. */
  start(platformId: number) {
    if (this.platformId === platformId && this.pollTimer) return;
    this.stop();
    this.platformId = platformId;
    void this.fetch();
    this.pollTimer = setInterval(() => void this.fetch(), 30_000);
  }

  stop() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  async fetch() {
    if (!this.platformId) return;
    try {
      const res = await api.get<Counters>('/api/v1/admin/sidebar/counters', {
        params: { platform_id: this.platformId },
      });
      this.ordersNew = res.data?.orders_new ?? 0;
      this.laundryPending = res.data?.laundry_pending ?? 0;
      this.fitnessPending = res.data?.fitness_pending ?? 0;
      this.conferenceInquiriesNew = res.data?.conference_inquiries_new ?? 0;
      const dn = res.data?.fitness_display_name;
      const ruRu = dn && typeof dn === 'object' ? dn['ru-RU'] : '';
      this.fitnessLabel = (ruRu || '').trim() || 'Wellness';

      // Параллельный запрос на inter-dept inbox-счётчик. Не валим
      // основной счётчик если упало.
      try {
        const count = await fetchInterDeptInboxCount(this.platformId);
        this.interdeptInbox = count;
      } catch {
        // тихо
      }
    } catch {
      // Тихо — это фоновый опрос, ошибки не показываем пользователю.
    }
  }
}

export const sidebarCountersStore = new SidebarCountersStore();
